import { ReactComponent as BookIcon } from 'assets/icons/icon-book-open.svg';
import Card from 'components/Card/Card';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { makeMutationForm } from 'components/makeMutationForm/makeMutationForm';
import Page from 'components/Page';
import ResetButton from 'components/ResetButton/ResetButton';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import { useGetBusinessUnitQuery } from 'components/ViewBusinessUnit/GetBusinessUnit.generated';
import React from 'react';
import { toast } from 'react-toastify';
import { useSearchParams, useWrappedUrqlMutation } from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import Stack from 'toolbox/src/components/Stack/Stack';
import {
  CreateCurrencyBookMutationVariables,
  useCreateCurrencyBookMutation,
} from './CreateCurrencyBook.generated';
import { useNavigate } from 'routeMap';
import PreventLeavingFormIfTouched from 'toolbox/src/components/PreventLeavingFormIfTouched/PreventLeavingFormIfTouched';
import GoBackButton from 'components/GoBackButton/GoBackButton';

const AddCurrencyBookForm = makeMutationForm<
  CreateCurrencyBookMutationVariables
>({
  config: {
    name: {
      type: 'text',
    },
    businessUnitId: {
      type: 'businessUnitId',
    },
    currencyId: {
      type: 'currencyId',
    },
  },
  validate: ({ name, currencyId, businessUnitId }) => {
    if (!name) {
      return { name: 'You must include the currency book name.' };
    }
    if (!currencyId) {
      return {
        currencyId: 'You must choose a currency.',
      };
    }
    if (!businessUnitId) {
      return {
        businessUnitId: 'You must choose a business unit.',
      };
    }
  },
});

export const AddCurrencyBook = () => {
  const navigate = useNavigate();
  const params = useSearchParams<{ businessUnitId?: string }>();

  const addBook = useWrappedUrqlMutation(useCreateCurrencyBookMutation, {
    onSuccess: (result) => {
      toast.info('Created currency book successfully.');
      navigate.viewCurrencyBookById({
        params: {
          id: result.insert_currency_books.returning?.[0].id,
        },
      });
    },
    onError: () => {},
  });

  const [result] = useGetBusinessUnitQuery({
    pause: !params?.businessUnitId,
    variables: { id: Number(params?.businessUnitId) },
  });

  return (
    <Page
      title={'Add New Currency Book'}
      icon={
        <BookIcon className="inline w-10 h-10 mr-2 -mb-1 -ml-2 text-gray-500" />
      }
    >
      <GoBackButton>Back To Business Unit</GoBackButton>
      <Card title="New Currency Book Details">
        <AddCurrencyBookForm.Wrapper
          onSubmit={addBook.dispatch}
          initialValues={{ businessUnitId: Number(params.businessUnitId) }}
        >
          <AddCurrencyBookForm.Inputs.name
            label="Name"
            className="max-w-xs"
            autoFocus
          />
          <AddCurrencyBookForm.Inputs.businessUnitId
            className="max-w-xs"
            label="Business Unit"
            initialOption={result.data?.business_units_by_pk}
          />
          <AddCurrencyBookForm.Inputs.currencyId
            className="max-w-xs"
            label="Currency"
          />

          <Stack>
            <Flex justifyContent="flex-end" mx={2}>
              <ResetButton>Reset</ResetButton>
              <SubmitButton>Save</SubmitButton>
            </Flex>
            <ErrorMessage {...addBook} />
          </Stack>
          <PreventLeavingFormIfTouched />
        </AddCurrencyBookForm.Wrapper>
      </Card>
    </Page>
  );
};
